import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, CircularProgress, FormControl, FormLabel, Grid, IconButton, List, ListItem, ListItemIcon, ListItemText, Paper, Popover, Typography, useTheme, } from "@mui/material";
import { useCallback, useContext, useEffect, useMemo, useRef, useState, } from "react";
import { NerdFileUploadContext } from "./nerdFileUploadWrapper";
import { nanoid } from "@reduxjs/toolkit";
import { Check, Clear, CloudUpload, Info } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { showNotification } from "../redux/notification";
import { useTranslation } from "react-i18next";
import nerdUII18n from "../i18n";
/**
 *
 * @param {NerdFileUploadProps} props Component props
 * @returns {ReactElement} NerdFileUpload
 */
export default function NerdFileUpload(props) {
    const context = useContext(NerdFileUploadContext);
    const id = useMemo(() => nanoid(), []);
    const { buttonLabel, buttonTitle, startIcon, dropZoneLabel, multiple, accept, maxSize, dense, hideFilesList, uploadFile, uploadedFilesList, browseFilesButtonProps, denseFilesListPopoverProps, acceptedFormatsText, acceptedFormatsTypographyProps, limitSizeText, limitSizeTypographyProps, fileErrorNotificationProps, } = props;
    const [anchorEl, setAnchorEl] = useState(null);
    const [filesMap, setFilesMap] = useState(new Map());
    const { t } = useTranslation("components", { i18n: nerdUII18n });
    const dispatch = useDispatch();
    const open = Boolean(anchorEl);
    const theme = useTheme();
    const listItems = useMemo(() => {
        {
            const items = [];
            filesMap.forEach((item, i) => {
                items.push(_jsxs(ListItem, { children: [_jsx(ListItemIcon, { children: !uploadedFilesList?.find((file) => file.name === item.file.name) && item.error == undefined ? (_jsx(CircularProgress, { size: "25px" })) : item.error != undefined ? (_jsx(Clear, { color: "error" })) : (_jsx(Check, { color: "success" })) }), _jsx(ListItemText, { primary: item.file.name, secondary: item.error || `${(item.file.size / 1000000).toFixed(2)} MB`, primaryTypographyProps: {
                                sx: { whiteSpace: "normal", wordBreak: "break-all" },
                            }, secondaryTypographyProps: {
                                color: item.error !== undefined
                                    ? theme.palette.error.main
                                    : theme.palette.grey[600],
                            } })] }, i));
            });
            return items;
        }
    }, [filesMap, uploadedFilesList]);
    useEffect(() => {
        context.subscribe(id, handleDrop, dropZoneLabel);
        return () => {
            context.unsubscribe(id);
        };
    }, []);
    const fileInput = useRef(null);
    const checkFile = useCallback((file) => {
        try {
            if (accept && !accept.includes(file.type)) {
                throw new Error("The file type is not allowed.");
            }
            else if (maxSize && file.size > maxSize) {
                throw new Error(`File size exceeds limit (${maxSize / 1000000} MB)`);
            }
            else {
                return true;
            }
        }
        catch (error) {
            if (error instanceof Error) {
                if (dense || hideFilesList) {
                    dispatch(showNotification({
                        ...fileErrorNotificationProps,
                        notification: {
                            uuid: nanoid(),
                            severity: "error",
                            variant: "non-intrusive",
                            autohide: true,
                            autoHideDuration: 2500,
                            ...fileErrorNotificationProps?.notification,
                            title: error.message,
                        },
                    }));
                }
                return error.message;
            }
            else {
                console.error(error);
                return "Unexpected error";
            }
        }
    }, [maxSize, accept]);
    /**
     *
     * @param {ChangeEvent<HTMLInputElement>} e ChangeEvent
     */
    function handleChange(e) {
        const newFiles = e.target.files;
        const filesToPush = new Map();
        const files = [];
        if (newFiles != null) {
            for (let i = 0; i < newFiles.length; i++) {
                const file = newFiles[i];
                const check = checkFile(file);
                filesToPush.set(file.name, {
                    file: file,
                    error: check === true ? undefined : check,
                });
                if (check === true)
                    files.push(file);
            }
            if (multiple)
                uploadFile(files);
            else
                uploadFile(files[0]);
        }
        e.target.value = "";
        setFilesMap(filesToPush);
    }
    /**
     *
     * @param {DragEvent<HTMLDivElement>} e Drag event
     */
    function handleDrop(e) {
        const filesToPush = new Map();
        const limit = multiple ? e.dataTransfer.files.length : 1;
        const files = [];
        for (let i = 0; i < limit; i++) {
            const file = e.dataTransfer.files[i];
            const check = checkFile(file);
            filesToPush.set(file.name, {
                file: file,
                error: check === true ? undefined : check,
            });
            if (check === true)
                files.push(file);
        }
        if (multiple)
            uploadFile(files);
        else
            uploadFile(files[0]);
        setFilesMap(filesToPush);
    }
    return (_jsxs(_Fragment, { children: [_jsxs(Grid, { container: true, spacing: 1, children: [_jsxs(Grid, { item: true, xs: 12, children: [_jsx("input", { type: "file", ref: fileInput, multiple: multiple, ...(accept ? { accept: accept.join(",") } : {}), style: { display: "none" }, onChange: handleChange }), _jsxs(Grid, { container: true, spacing: 1, alignItems: "flex-end", children: [_jsx(Grid, { item: true, children: _jsxs(FormControl, { children: [_jsx(FormLabel, { children: buttonLabel }), _jsx(Button, { variant: "outlined", startIcon: startIcon ?? _jsx(CloudUpload, {}), ...browseFilesButtonProps, onClick: (e) => {
                                                        fileInput.current?.click();
                                                        browseFilesButtonProps?.onClick?.(e);
                                                    }, children: buttonTitle ?? t("nerdFileUpload.browse_files") })] }) }), dense && filesMap.size > 0 && !hideFilesList ? (_jsx(Grid, { item: true, children: _jsx(IconButton, { onClick: (e) => {
                                                setAnchorEl(anchorEl ? null : e.currentTarget);
                                            }, children: _jsx(Info, {}) }) })) : null] })] }), _jsxs(Grid, { item: true, xs: 12, children: [_jsx(Typography, { variant: "caption", ...acceptedFormatsTypographyProps, children: accept
                                    ? acceptedFormatsText
                                        ? acceptedFormatsText
                                        : // eslint-disable-next-line i18next/no-literal-string
                                            `Accepted formats: ${accept.join(", ")}`
                                    : "" }), _jsx(Typography, { variant: "caption", display: "block", ...limitSizeTypographyProps, children: maxSize
                                    ? limitSizeText
                                        ? limitSizeText
                                        : `Limit: ${maxSize / 1000000} MB`
                                    : "" })] })] }), filesMap.size > 0 && !hideFilesList ? (dense ? (_jsx(Popover, { anchorOrigin: {
                    vertical: "center",
                    horizontal: "right",
                }, PaperProps: {
                    sx: {
                        width: 400,
                    },
                }, open: open, anchorEl: anchorEl, ...denseFilesListPopoverProps, onClose: (e, reason) => {
                    setAnchorEl(null);
                    denseFilesListPopoverProps?.onClose?.(e, reason);
                }, onDragEnter: (e) => {
                    setAnchorEl(null);
                    denseFilesListPopoverProps?.onDragEnter?.(e);
                }, children: _jsx(List, { dense: true, children: listItems }) })) : (_jsx(Paper, { sx: {
                    mt: 2,
                }, children: _jsx(List, { dense: true, children: listItems }) }))) : null] }));
}
